import _merge from 'lodash/merge';
import _memoize from 'lodash/memoize';
import { Utils } from '@lightningjs/sdk';
import { getKeyCodes, getSettings as getPlatformSettings } from './platform/default';

export const STATIC_DIRECTORY = 'static';

export const capitalizeKeyActions = _memoize(codes =>
  Object.keys(codes).reduce((previous, key) => {
    const value = codes[key];
    previous[key] = value.charAt(0).toUpperCase() + value.slice(1);

    return previous;
  }, {}),
);

export const getSettings = (appSettings = undefined, platformSettings = undefined) => {
  const sizes =
    window.innerHeight <= 720
      ? {
          w: 1280,
          h: 720,
        }
      : {
          w: 1920,
          h: 1080,
        };

  const forcedStageSettings = {
    // Disable UI auto-scaling
    precision: 1,
    // Ensure video will be visible behind an app
    clearColor: 0x00000000,
  };

  let canvas2d;
  let inspector;
  try {
    const query = new URL(document.location).searchParams;
    canvas2d = query.get('canvas2d') === 'true';
    inspector = query.get('inspector') === 'true';
  } catch (error) {
    canvas2d = false;
    inspector = false;
  }

  const commonSettings = {
    appSettings: {
      keys: capitalizeKeyActions(getKeyCodes()),
      stage: {
        useImageWorker: true,
        canvas2d,
        ...sizes,
      },
      debug: false,
    },
    platformSettings: {
      inspector,
      path: `./${STATIC_DIRECTORY}`,
      log: true,
      showVersion: false,
      router: {
        disableTransitions: false,
        lazyCreate: true,
        lazyDestroy: true,
        destroyOnHistoryBack: true,
        reuseInstance: false,
      },
    },
  };

  // Gracefully apply any overrides except sizes: we need to keep our own resolution handling in order to follow the Studio design
  const overrides = _merge(
    {
      appSettings,
      platformSettings,
    },
    {
      appSettings: {
        stage: {
          ...forcedStageSettings,
        },
      },
    },
  );

  commonSettings.platformSettings.fontLoader = (fonts, store) =>
    Promise.allSettled(
      fonts.map(({ family, url, urls, descriptors }) => {
        const src = urls ? urls.map(url => `url(${url})`) : `url(${url})`;
        const fontFace = new FontFace(family, src, descriptors || {});
        store.push(fontFace);
        document.fonts.add(fontFace);

        return fontFace.load();
      }),
    );

  return _merge(commonSettings, getPlatformSettings(), overrides);
};

const STATIC_DIRECTORY_REG_EXP = new RegExp(`^/${STATIC_DIRECTORY}/`);

// For some reason Lightning app store always adds "/static" at the very end of assets base URL
// It creates a duplicate since url-loader adds the same path as prefix
export const getFullPath = url => {
  if (!url) {
    return '';
  }

  const hostedUrlPublicPath = process.env.PUBLIC_PATH || '';
  const base = Utils.asset(url.replace(hostedUrlPublicPath, '/').replace(STATIC_DIRECTORY_REG_EXP, ''));

  // Handling deeplink scenarios which are not working on previews (AWS limitation)
  const { pathname } = window.location;
  if (!pathname || pathname === '/') {
    return base;
  }

  return process.env.IS_PREVIEW ? base : base.replace(pathname, '');
};
